import { initials } from './shared/initials.ts';
import { TwoFactorStep } from './account/TwoFactorStep.tsx';
import { providerStart, useSignIn, type SignedIn } from './login-model.ts';
import { GoogleMark, MicrosoftMark } from './ProviderMarks.tsx';

/**
 * Signing in (decision 19), as Riverstone draws it: a password, Google, or a one-time code by email or
 * text, as the company allows (/auth/methods). Blueprint draws SignInPage.tsx; both run login-model.ts.
 */
export function Login({
  onSignedIn,
  tenant: knownTenant,
  again = false,
  brand,
}: {
  /** recoveryCodesLeft: set when the second step was a recovery code, so the app can say how many remain. */
  onSignedIn: SignedIn;
  /** Signing in again after the session ended: the company is known and the screen stays behind. */
  tenant?: string;
  again?: boolean;
  /**
   * The company, once it is known before sign-in (the company-host branding lookup, still to come): its
   * name for the "to <company>" line and its logo stone. Without it the page is unbranded, as today.
   */
  brand?: { name: string; logoUrl?: string | null };
}) {
  const s = useSignIn({ onSignedIn, tenant: knownTenant, again });
  const { email, setEmail, mobile, setMobile, password, setPassword, code, setCode, tenant, setTenant } = s;
  const { mode, setMode, error, busy, second, stepCompany, company } = s;
  const shown = brand ?? (second && stepCompany ? { name: stepCompany, logoUrl: null } : undefined);
  const { google, microsoft, byEmail, byText, byPassword } = s;
  const companyField = s.asksCompany && (
    <label>
      Company
      <input
        className="field"
        value={tenant}
        onChange={(e) => setTenant(e.target.value)}
        autoComplete="organization"
        aria-describedby="login-company-hint"
        required
      />
      <span id="login-company-hint" className="help">
        The company name from your invite
      </span>
    </label>
  );
  // the refusal sits above everything, in the bad tone (its text is the server's answer, unchanged)
  const banner = error && (
    <p className="banner bad login-banner" role="alert">
      {error}
    </p>
  );

  return (
    <main
      className={again ? 'login again' : 'login'}
      role={again ? 'dialog' : undefined}
      aria-modal={again || undefined}
    >
      <div className="sheet login-sheet">
        {shown && (
          <span className="logo login-logo" aria-hidden="true">
            {shown.logoUrl ? <img src={shown.logoUrl} alt="" /> : initials(shown.name)}
          </span>
        )}
        {!second && <h1>Sign in</h1>}
        {brand && !second && <p className="lede login-lede">to {brand.name}</p>}
        {again && <p className="muted">Your session ended. Sign in again to carry on where you were.</p>}
        {banner}
        {second ? (
          <TwoFactorStep
            methods={second}
            {...(shown ? { company: shown.name } : {})}
            {...(company ? { tenant: company } : {})}
            onSignedIn={onSignedIn}
            onCancel={s.startAgain}
          />
        ) : (
          <>
            {(google || microsoft) && (
              <>
                <div className="login-providers" role="group" aria-label="Sign in with">
                  {google && (
                    <a className="btn wide outline" href={providerStart('google', company)}>
                      <GoogleMark />
                      Continue with Google
                    </a>
                  )}
                  {microsoft && (
                    <a className="btn wide outline" href={providerStart('microsoft', company)}>
                      <MicrosoftMark />
                      Continue with Microsoft
                    </a>
                  )}
                </div>
                <p className="login-or" aria-hidden="true">
                  or
                </p>
              </>
            )}
            {mode.kind === 'password' ? (
              <form className="login-form" onSubmit={(e) => void s.submitPassword(e)}>
                {companyField}
                <label>
                  Email
                  <input
                    className="field"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="username"
                    required
                  />
                </label>
                {byPassword && (
                  <label>
                    Password
                    <input
                      className="field"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      required
                    />
                  </label>
                )}
                {byPassword && (
                  <button className="btn primary wide lg" disabled={busy}>
                    {busy ? 'Signing in…' : 'Sign in'}
                  </button>
                )}
              </form>
            ) : (
              <form className="login-form" onSubmit={(e) => void s.submitCode(e)}>
                {companyField}
                {mode.channel === 'email' ? (
                  <label>
                    Email
                    <input
                      className="field"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="username"
                      required
                      readOnly={!!mode.sent}
                    />
                  </label>
                ) : (
                  <label>
                    Mobile
                    <input
                      className="field"
                      type="tel"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value)}
                      autoComplete="tel"
                      required
                      readOnly={!!mode.sent}
                    />
                  </label>
                )}
                {mode.sent && (
                  <>
                    <p className="notice ok" role="status">
                      {mode.sent}
                    </p>
                    <label>
                      Code
                      <input
                        className="field"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        required
                      />
                    </label>
                  </>
                )}
                <button className="btn primary wide lg" disabled={busy}>
                  {busy
                    ? 'One moment…'
                    : mode.sent
                      ? 'Sign in'
                      : mode.channel === 'email'
                        ? 'Email me a code'
                        : 'Text me a code'}
                </button>
                {mode.sent && (
                  <button
                    type="button"
                    className="btn quiet"
                    onClick={() => setMode({ ...mode, sent: null })}
                  >
                    Send a new code
                  </button>
                )}
              </form>
            )}
            <div className="login-other" role="group" aria-label="Other ways to sign in">
              {byEmail && !(mode.kind === 'code' && mode.channel === 'email') && (
                <button
                  type="button"
                  className="lnk"
                  onClick={() => s.switchTo({ kind: 'code', channel: 'email', sent: null })}
                >
                  Email me a code
                </button>
              )}
              {byText && !(mode.kind === 'code' && mode.channel === 'sms') && (
                <button
                  type="button"
                  className="lnk"
                  onClick={() => s.switchTo({ kind: 'code', channel: 'sms', sent: null })}
                >
                  Text me a code
                </button>
              )}
              {!byEmail && mode.kind === 'password' && (
                // The owner can always get in with an email code, whatever the company allows.
                <button
                  type="button"
                  className="lnk"
                  onClick={() => s.switchTo({ kind: 'code', channel: 'email', sent: null })}
                >
                  Company owner? Email me a code
                </button>
              )}
              {byPassword && mode.kind === 'code' && (
                <button type="button" className="lnk" onClick={() => s.switchTo({ kind: 'password' })}>
                  Use my password
                </button>
              )}
              {byPassword && byEmail && mode.kind === 'password' && (
                <p className="small muted">Forgot your password? An email code signs you in.</p>
              )}
            </div>
          </>
        )}
      </div>
    </main>
  );
}
